import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    loginSuccess,
    updateMembers,
    setTurnPlayer,
    setPendingGameStatus,
    setTurnNumber,
    startGame,
    setGameStarted,
    setPartnerChoiceData,
    setPlayerPaused,
} from '@/features/running-game/runningGameSlice';
import { gameApiSlice } from '@/features/game/gameApiSlice';

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('Initial');
  const messageListeners = useRef(new Set());
  const dispatch = useDispatch();

  const connect = useCallback((groupId) => {
    if (!groupId) {
      console.error("Connect function called without a groupId.");
      return;
    }
    
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      console.log("WebSocket is already connected.");
      setConnectionStatus('Open');
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const baseUrl = import.meta.env.VITE_WEBSOCKET_URL || `${protocol}//${host}:8002`;
    const wsUrl = `${baseUrl}/ws/game/${groupId}/`;
    console.log(`[WebSocketContext] Attempting to connect to: ${wsUrl}`);
    setConnectionStatus('Connecting');
    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onopen = () => {
      console.log('[WebSocketContext] WebSocket connection established.');
      setConnectionStatus('Open');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'player.login_response':
          if (data.payload.success) {
            dispatch(loginSuccess({
              player: data.payload.player,
              group: data.payload.group,
              members: data.payload.members,
              gameRunId: data.payload.game_run_id,
              gameToken: data.payload.game_token,
              is_turn_player: data.payload.is_turn_player,
              status: data.payload.status
            }));
          }
          break;
        case 'game.players_ready_update':
          dispatch(updateMembers({ members: data.payload.members }));
          break;
        case 'game.partner_chosen':
          dispatch(setPartnerChoiceData({ 
            selectedExample: data.payload.selected_example, 
            gameScale: data.payload.game_scale 
          }));
          break;
        case 'game.start_turn':
          dispatch(startGame({ active_player_id: data.payload.active_player.id }));
          break;
        case 'turn.update':
          dispatch(setTurnPlayer({ next_player_id: data.payload.next_player_id }));
          break;
        case 'game.status_update':
          console.log('[WebSocketContext] Game status update received:', data.payload.status);
          dispatch(setPendingGameStatus(data.payload.status)); // Set the pending status
          break;
        case 'player.turn_started':
          dispatch(setTurnPlayer({ next_player_id: data.payload.active_player_id }));
          if (data.payload.turn_number) {
            dispatch(setTurnNumber(data.payload.turn_number));
          }
          break;
        case 'game.next_turn_ready':
          if (data.payload.next_player && data.payload.start_countdown) {
            dispatch(setTurnPlayer({ next_player_id: data.payload.next_player.id }));
          }
          break;
        case 'game.all_to_waiting':
            if (data.payload.next_player) {
                dispatch(setTurnPlayer({ next_player_id: data.payload.next_player.id }));
                // Invalidate the cache for partner choices to force a refetch
                dispatch(gameApiSlice.util.invalidateTags(['GameExample']));
            }
            break;
        case 'game.player_paused':
            console.log('[WebSocketContext] Player paused:', data.payload.player_name);
            dispatch(setPlayerPaused({
              paused_player_id: data.payload.player_id,
              is_paused: true,
              members: data.payload.members
            }));
            break;
        case 'game.player_resumed':
            console.log('[WebSocketContext] Player resumed:', data.payload.player_name);
            dispatch(setPlayerPaused({
              paused_player_id: data.payload.player_id,
              is_paused: false,
              members: data.payload.members
            }));
            break;
      }

      messageListeners.current.forEach(listener => listener(data));
    };

    ws.onclose = () => {
      console.log('WebSocket Disconnected');
      setConnectionStatus('Closed');
    };

    ws.onerror = (error) => {
      console.error('WebSocket Error:', error);
      setConnectionStatus('Error');
    };

    return () => {
      ws.close();
    };
  }, [dispatch]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.onclose = null; // prevent handler from firing
      socketRef.current.close();
      socketRef.current = null;
      setConnectionStatus('Closed');
    }
  }, []);

  const sendMessage = (message) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected.');
    }
  };

  const addMessageListener = useCallback((listener) => {
    messageListeners.current.add(listener);
  }, []);

  const removeMessageListener = useCallback((listener) => {
    messageListeners.current.delete(listener);
  }, []);

  const value = {
    connectionStatus,
    sendMessage,
    disconnect,
    addMessageListener,
    removeMessageListener,
    connect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
